import {
  mdiArrowLeft,
  mdiEmail,
  mdiLockQuestion,
  mdiLogout,
  mdiWeatherNight,
  mdiWeatherSunny,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, TextField } from '@mollybet/ui';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import useDispatch from 'common/hooks/useDispatch';
import useSelector from 'common/hooks/useSelector';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { api, defaultAPIRequestState } from 'api';

import { updateEmailRecipe, updatePasswordRecipe } from '../localState/recipes';
import { doUpdateEmail, doUpdatePassword } from '../localState/thunkActions';
import { doLogout } from 'common/state/auth/thunkActions';
import calculatePermissions from 'common/utils/calculatePermissions';

const Dropdown = styled.div`
  position: absolute;
  top: 4rem;
  right: 1rem;
  width: 320px;
  background-color: ${(props) => props.theme.cardLevel1};
  color: ${(props) => props.theme.foreground};
  z-index: 9000;
  box-shadow: ${(props) => props.theme.shadows[1]};
  border-radius: 4px;
  padding: 1rem;
  overflow: hidden;
  transition: height 500ms ease;

  & > div {
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0rem;
    }
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: background 500ms ease;
  padding: 0.5rem;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.cardLevel2};
  }
`;

const IconButton = styled.div`
  width: 1rem;
  height: 1rem;
  background-color: ${(props) => props.theme.background};
  color: ${(props) => props.theme.foreground};
  border-radius: 50%;
  padding: 0.5rem;
  margin: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 300ms;
`;

const NavText = styled.div`
  font-weight: bold;
  margin-left: 1rem;
`;

const NavDivider = styled.div`
  border-bottom: solid 1px ${(props) => props.theme.disabled};
`;

const Form = styled.form`
  padding: 0px 20px;
`;

const FormError = styled.div`
  margin: 10px 0px;
  color: ${(props) => props.theme.danger};
`;

const FormSuccess = styled.div`
  margin: 10px 0px;
  color: ${(props) => props.theme.primary};
`;

const InputField = styled(TextField)`
  width: 100%;
`;

const SpacedInputField = styled(InputField)`
  margin-bottom: 5px;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 15px;
  margin-bottom: 20px;
`;

type MenuMode = 'home' | 'email' | 'password';

const includesAny = (array1: Array<unknown>, array2: Array<unknown>) =>
  array1.some((i) => array2.includes(i));

function SettingsDropdown() {
  const [mode, setMode] = useState<MenuMode>('home');
  const { theme } = useSelector((state) => state.theme);
  const adminUrl = useSelector((state) =>
    state.config?.config?.urls?.admin
      ? state.config.config.urls.admin.endsWith('/')
        ? state.config.config.urls.admin.slice(0, -1)
        : state.config.config.urls.admin
      : null
  );
  // eslint-disable-next-line
  // @ts-ignore
  const { impersonator, groups } = useSelector((state) => state.auth.user);
  const permissions = useMemo(() => calculatePermissions(groups), [groups]);

  const dispatch = useDispatch();
  const unsudo = async (next: string = '/') =>
    api
      .post('/contact/unswitch/')
      .catch(() => null)
      .finally(() => (window.location.href = next));

  return (
    <Dropdown>
      {mode === 'home' && (
        <>
          <NavItem onClick={() => setMode('email')}>
            <IconButton>
              <Icon size={1} path={mdiEmail} />
            </IconButton>
            <NavText>Update E-Mail</NavText>
          </NavItem>
          <NavItem onClick={() => setMode('password')}>
            <IconButton>
              <Icon size={1} path={mdiLockQuestion} />
            </IconButton>
            <NavText>Update Password</NavText>
          </NavItem>
          <NavDivider />
          <NavItem
            onClick={() =>
              dispatch({
                type: 'theme/change',
                theme: theme === 'light' ? 'dark' : 'light',
              })
            }
          >
            <IconButton>
              <Icon
                size={1}
                path={theme === 'light' ? mdiWeatherNight : mdiWeatherSunny}
              />
            </IconButton>
            <NavText>
              {theme === 'light'
                ? 'Switch to Dark Mode'
                : 'Switch to Light Mode'}
            </NavText>
          </NavItem>

          {includesAny(permissions, ['admin']) && (
            <NavItem
              onClick={() =>
                unsudo(`${adminUrl}/totus/contact/?groups__id__exact=2`)
              }
            >
              <IconButton style={{ background: 'green' }}>
                <Icon size={1} path={mdiLogout} />
              </IconButton>
              <NavText>Sudo</NavText>
            </NavItem>
          )}

          {impersonator && (
            <NavItem onClick={() => unsudo()}>
              <IconButton style={{ background: 'red' }}>
                <Icon size={1} path={mdiLogout} />
              </IconButton>
              <NavText>Unsudo</NavText>
            </NavItem>
          )}
          <NavItem onClick={() => dispatch(doLogout())}>
            <IconButton>
              <Icon size={1} path={mdiLogout} />
            </IconButton>
            <NavText>Logout</NavText>
          </NavItem>
        </>
      )}
      {mode === 'email' && (
        <>
          <NavItem onClick={() => setMode('home')}>
            <IconButton>
              <Icon size={1} path={mdiArrowLeft} />
            </IconButton>
          </NavItem>
          <EmailChangeForm />
        </>
      )}
      {mode === 'password' && (
        <>
          <NavItem onClick={() => setMode('home')}>
            <IconButton>
              <Icon size={1} path={mdiArrowLeft} />
            </IconButton>
          </NavItem>
          <PasswordChangeForm />
        </>
      )}
    </Dropdown>
  );
}

function EmailChangeForm() {
  const [email, setEmail] = useState('');
  const [state, dispatch] = useProducerWithThunks(
    updateEmailRecipe,
    defaultAPIRequestState
  );

  // Email submission callback for form
  const submitUpdateEmail: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(doUpdateEmail(email));
    },
    [dispatch, email]
  );

  return (
    <Form onSubmit={submitUpdateEmail}>
      {state.success && <FormSuccess>E-Mail updated successfully!</FormSuccess>}
      {state.error && <FormError>{state.error}</FormError>}
      <InputField
        shrinked
        label="New E-Mail *"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <SubmitButton type="submit" variant="primary" disabled={!email}>
        Update E-Mail
      </SubmitButton>
    </Form>
  );
}

function PasswordChangeForm() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');

  const [state, dispatch] = useProducerWithThunks(
    updatePasswordRecipe,
    defaultAPIRequestState
  );

  // Password submission callback for form
  const submitUpdatePassword: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(doUpdatePassword(newPassword1, oldPassword));
    },
    [dispatch, newPassword1, oldPassword]
  );

  const matchingPasswords = useMemo(
    () => newPassword1 === newPassword2,
    [newPassword1, newPassword2]
  );

  return (
    <Form onSubmit={submitUpdatePassword}>
      {state.success && (
        <FormSuccess>Password updated successfully!</FormSuccess>
      )}
      {state.error && <FormError>{state.error}</FormError>}
      {!matchingPasswords && (
        <FormError>New password fields do not match!</FormError>
      )}
      <SpacedInputField
        shrinked
        type="password"
        label="Current Password *"
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
      />
      <SpacedInputField
        shrinked
        type="password"
        label="New Password *"
        value={newPassword1}
        onChange={(e) => setNewPassword1(e.target.value)}
      />
      <InputField
        shrinked
        type="password"
        label="Confirm New Password *"
        value={newPassword2}
        onChange={(e) => setNewPassword2(e.target.value)}
      />
      <SubmitButton
        type="submit"
        variant="primary"
        disabled={!oldPassword || !matchingPasswords}
      >
        Update Password
      </SubmitButton>
    </Form>
  );
}

export default SettingsDropdown;
